/*eslint-disable*/
import { useMemo, useState } from 'react'
import { Button } from '@/Components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/Components/ui/popover'
import { Input } from '@/Components/ui/input'
import { Check, ChevronDown, Search } from 'lucide-react'
import { useAnalysisStore } from './AssetAnalysisStore'
import { useShallow } from 'zustand/react/shallow'

const ParameterSelector = () => {
    const { 
        analysisParameters, 
        selectedAnalysisParameter, 
        setSelectedAnalysisParameter,
        activeAnalysisTab
    } = useAnalysisStore(useShallow(state => ({
        analysisParameters: state.analysisParameters,
        selectedAnalysisParameter: state.selectedAnalysisParameter,
        setSelectedAnalysisParameter: state.setSelectedAnalysisParameter,
        activeAnalysisTab: state.activeAnalysisTab
    })));

    const [searchQuery, setSearchQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const toggleParameter = (key) => {
        setSelectedAnalysisParameter(key);
        setIsOpen(false);
    }

    const selectAll = () => {
        setSelectedAnalysisParameter('all');
        setIsOpen(false);
    }

    const selectedParam = analysisParameters.find(
        param => param.key === selectedAnalysisParameter
    );

    const categorizedParameters = useMemo(() => {
        const result = {
            'AI supervisor checks': [],
            'UY App checklist': []
        };

        analysisParameters.forEach(param => {
            if (param.key !== 'all') {
                if (param.category === 'AI supervisor checks') {
                    result['AI supervisor checks'].push(param);
                } else {
                    result['UY App checklist'].push(param);
                }
            }
        });

        return result;
    }, [analysisParameters]);

    const filteredParameters = useMemo(() => {
        if (!searchQuery.trim()) {
            return categorizedParameters;
        }

        const result = {};
        
        Object.entries(categorizedParameters).forEach(([category, params]) => {
            const filtered = params.filter(param => 
                param.label.toLowerCase().includes(searchQuery.toLowerCase())
            );
            
            if (filtered.length > 0) {
                result[category] = filtered;
            }
        });

        return result;
    }, [categorizedParameters, searchQuery]);

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant='outline'
                    role='combobox'
                    aria-expanded={isOpen}
                    className='w-full !py-1 !text-xs justify-between border-gray-300 h-8'
                >
                    {selectedParam?.label || 'All parameters'}
                    <ChevronDown className='ml-2 h-3 w-3 shrink-0 opacity-50'/>
                </Button>
            </PopoverTrigger>
            <PopoverContent className='w-[300px] p-0'>
                <div className='px-3 py-2'>
                    <div className='relative'>
                        <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground'/>
                        <Input
                            placeholder='Search parameter...'
                            className='pl-8'
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
                <div className='max-h-[300px] overflow-auto'>
                    {Object.entries(filteredParameters).map(([category, params]) => (
                        <div key={category}>
                            <div className='bg-muted px-3 py-1.5 text-sm text-slate-400 font-medium'>
                                {category}
                            </div>
                            {params.map((param) => (
                                <div
                                    key={param.key}
                                    className='flex items-center justify-between px-3 py-2 pl-10 hover:bg-muted cursor-pointer'
                                    onClick={() => toggleParameter(param.key)}
                                >
                                    <span>{param.label}</span>
                                    {param.key === selectedAnalysisParameter && (
                                        <Check className='h-4 w-4 text-black'/>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                {activeAnalysisTab !== 'statistical' && (
                    <div className='border-t px-3 py-2'>
                        <Button
                            variant='ghost'
                            className='w-full justify-between text-sm text-muted-foreground'
                            onClick={selectAll}
                        >
                            All parameters
                            <span>Select all</span>
                        </Button>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    )
}

export default ParameterSelector; 